import * as React from 'react';
import { SurfaceType } from '../types/SurfaceType';
import { ActionKey } from 'owa-outlook-service-options';
import { AddinCommandSurfaceItem } from 'owa-addins-types';
import { CommandBarButton } from 'office-ui-fabric-react/lib/Button';
import { ContextualMenuItemType } from 'office-ui-fabric-react/lib/ContextualMenu';
import { ICheckboxProps } from 'office-ui-fabric-react/lib/Checkbox';
import { Icon, IconType, IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { Image } from 'office-ui-fabric-react/lib/Image';
import { IOverflowSetItemProps, OverflowSet } from 'office-ui-fabric-react/lib/OverflowSet';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { observer } from 'mobx-react';
import { OptionDescription, OptionsCheckbox } from 'owa-options-controls';
import { shouldAddActionToEnd } from 'owa-surface-actions';
import { SurfaceActionIcon } from 'owa-surface-actions/lib/utils/getIconNameFromKey';

const DIVIDER_MENU_ITEM_NAME = '-';

let styles = require('./SurfaceActionsShared.scss');
let classNames = require('classnames/bind').bind(styles);

export interface SurfaceActionsSharedProps {
    actions: ActionKey[];
    addins: AddinCommandSurfaceItem[];
    selectedActions: string[];
    selectedAddins: string[];
    onActionSelected: (actionCollection: string[], actionKey: string, isSelected: boolean) => void;
    onAddinSelected: (addinCollection: string[], addinKey: string, isSelected: boolean) => void;
    getDisplayNameFromAction(action: ActionKey): string;
    getIconNameFromAction(action: ActionKey): SurfaceActionIcon;
    isLeftToRight: boolean;
    previewImageUri: string;
    previewClassName: string;
    maxPreviewItemCount: number;
    description: string;
    surfaceType?: SurfaceType;
}

interface SurfaceActionsPreviewItem extends IOverflowSetItemProps {
    isAddin: boolean;
    surfaceType?: SurfaceType;
}

@observer
export default class SurfaceActionsShared extends React.Component<SurfaceActionsSharedProps, {}> {
    render() {
        let { previewImageUri, previewClassName, description } = this.props;

        let actionCheckboxes: JSX.Element[] = [];
        let addinCheckboxes: JSX.Element[] = [];
        let previewPinnedItems: SurfaceActionsPreviewItem[] = [];
        let previewOverflowItems: SurfaceActionsPreviewItem[] = [];

        this.prepareActionsContent(actionCheckboxes, previewPinnedItems, previewOverflowItems);
        this.prepareAddinsContent(addinCheckboxes, previewPinnedItems, previewOverflowItems);

        return (
            <div className={styles.sharedWrapper}>
                <OptionDescription>{description}</OptionDescription>
                <div className={styles.sharedPreviewSection}>
                    <Image src={previewImageUri} className={'flipForRtl'} />
                    <OverflowSet
                        className={previewClassName}
                        items={previewPinnedItems}
                        overflowItems={previewOverflowItems}
                        onRenderItem={this.onRenderPreviewItem}
                        onRenderOverflowButton={this.onRenderPreviewOverflow}
                    />
                </div>
                <div className={styles.sharedListSection}>
                    {actionCheckboxes}
                    {addinCheckboxes}
                </div>
            </div>
        );
    }

    private prepareActionsContent(
        actionCheckboxes: JSX.Element[],
        previewPinnedItems: SurfaceActionsPreviewItem[],
        previewOverflowItems: SurfaceActionsPreviewItem[]
    ) {
        let { actions, selectedActions, onActionSelected } = this.props;
        actions.forEach(action => {
            let isPinned = selectedActions.indexOf(action) >= 0;
            if (action != DIVIDER_MENU_ITEM_NAME) {
                // If the action isn't a divider, create a OptionsCheckbox for it.
                actionCheckboxes.push(
                    <OptionsCheckbox
                        checked={isPinned}
                        onChange={(event, checked) => {
                            onActionSelected(selectedActions, action, checked);
                        }}
                        key={action}
                        onRenderLabel={props => {
                            return this.onRenderActionCheckboxLabel(props, action);
                        }}
                        styles={{ label: styles.checkBoxLabel, root: styles.checkBoxRoot }}
                    />
                );
                // If the action is pinned, push a preview of it to the proper collection
                if (isPinned) {
                    let { fallbackIconName, isImage } = this.props.getIconNameFromAction(action);
                    let previewAction: SurfaceActionsPreviewItem = {
                        key: action,
                        isAddin: false,
                        surfaceType: this.props.surfaceType,
                        iconProps: {
                            iconName: fallbackIconName,
                            iconType: isImage ? IconType.Image : IconType.default,
                            imageProps: isImage
                                ? { shouldFadeIn: false, src: fallbackIconName }
                                : null,
                        },
                        name: this.props.getDisplayNameFromAction(action),
                    };
                    this.addPreviewItem(previewAction, previewPinnedItems, previewOverflowItems);
                }
            }
        });
    }

    private prepareAddinsContent(
        addinCheckboxes: JSX.Element[],
        previewPinnedItems: SurfaceActionsPreviewItem[],
        previewOverflowItems: SurfaceActionsPreviewItem[]
    ) {
        let { addins, selectedAddins, onAddinSelected } = this.props;
        addins.forEach(addin => {
            let isPinned = selectedAddins.indexOf(addin.key) >= 0;
            if (addin.itemType == ContextualMenuItemType.Normal) {
                addinCheckboxes.push(
                    <OptionsCheckbox
                        checked={isPinned}
                        onChange={(event, checked) => {
                            onAddinSelected(selectedAddins, addin.key, checked);
                        }}
                        key={addin.key}
                        onRenderLabel={props => {
                            return this.onRenderAddinCheckboxLabel(
                                props,
                                addin.iconProps,
                                addin.name
                            );
                        }}
                        styles={{ label: styles.checkBoxLabel, root: styles.checkBoxRoot }}
                    />
                );
                // If the addin is pinned, push a preview of it to the proper collection
                if (isPinned) {
                    let previewAddin: SurfaceActionsPreviewItem = {
                        key: addin.key,
                        isAddin: true,
                        iconProps: addin.iconProps,
                        name: addin.name,
                    };
                    this.addPreviewItem(previewAddin, previewPinnedItems, previewOverflowItems);
                }
            }
        });
    }

    private addPreviewItem(
        item: SurfaceActionsPreviewItem,
        pinnedItems: SurfaceActionsPreviewItem[],
        overflowItems: SurfaceActionsPreviewItem[]
    ) {
        let { isLeftToRight, maxPreviewItemCount } = this.props;
        // If we have fewer than the max pinned items and no overflow items, push to the pinned collection.
        if (pinnedItems.length < maxPreviewItemCount && overflowItems.length == 0) {
            if (isLeftToRight || shouldAddActionToEnd(item.key as ActionKey)) {
                pinnedItems.push(item);
            } else {
                pinnedItems.unshift(item);
            }
        } else if (pinnedItems.length == maxPreviewItemCount) {
            // Otherwise, if we have exactly the max amount of pinned items, remove one and add it to the overflow along with the new item.
            overflowItems.push(isLeftToRight ? pinnedItems.pop() : pinnedItems.shift());
            overflowItems.push(item);
        } else {
            // Otherwise push to the overflow.
            overflowItems.push(item);
        }
    }

    private onRenderActionCheckboxLabel = (props: ICheckboxProps, key: ActionKey) => {
        let label = this.props.getDisplayNameFromAction(key);
        let { iconName, isImage } = this.props.getIconNameFromAction(key);
        let className = classNames(styles.checkbox, isImage ? styles.imageWrapper : '');
        return (
            <div className={className}>
                {isImage ? (
                    <Image className={styles.actionImage} shouldFadeIn={false} src={iconName} />
                ) : (
                    <Icon iconName={iconName} className={styles.actionIcon} />
                )}
                <Label className={styles.actionLabel}> {label} </Label>
            </div>
        );
    };

    private onRenderAddinCheckboxLabel = (
        props: ICheckboxProps,
        iconProps: IIconProps,
        name: string
    ) => {
        const { iconType } = iconProps;
        return (
            <div className={styles.checkbox}>
                <Icon
                    {...iconProps}
                    className={iconType == IconType.default ? styles.addinIcon : styles.addinImage}
                />
                <Label className={styles.actionLabel}> {name} </Label>
            </div>
        );
    };

    private onRenderPreviewItem = (item: SurfaceActionsPreviewItem): JSX.Element => {
        let isImage = item.iconProps && item.iconProps.iconType != IconType.default;
        let buttonClassName = classNames(styles.previewButton, {
            isImage: isImage,
            isAddin: item.isAddin,
            isHoverIcon: item.surfaceType == SurfaceType.Hover,
        });
        let iconClassName = classNames(styles.previewIcon, {
            isImage: isImage,
            isAddin: item.isAddin,
            isHoverIcon: item.surfaceType == SurfaceType.Hover,
        });
        return (
            <CommandBarButton
                onClick={null}
                iconProps={item.iconProps}
                styles={{ root: buttonClassName, icon: iconClassName }}
            />
        );
    };

    private onRenderPreviewOverflow = (overflowItems: SurfaceActionsPreviewItem[]): JSX.Element => {
        overflowItems.forEach(item => {
            if (!item.isAddin) {
                item.iconProps = null; // Hide static action icons in the overflow
            }
        });
        return (
            <CommandBarButton
                menuIconProps={{ iconName: 'ChevronDown' }}
                menuProps={{ items: overflowItems }}
                styles={{ root: classNames(styles.previewButton, styles.previewOverflowButton) }}
            />
        );
    };
}
